import React, { useEffect, useState } from 'react';
import { resolveMediaUrl } from '../../helpers/userAvatar';

/**
 * ListItem
 * ------------------------------------------------------------------
 * One generic row primitive reused across several dashboard widgets:
 *  - Today's Primary Focus  → leadingIcon + title + subtitle + trailing avatars
 *  - Active Session          → leadingIcon + title + subtitle(badge) + trailing avatars
 *  - Recent Activity         → dotColor + title + timestamp
 *  - Active Collaborators    → avatar + title + trailing role text
 *
 * Only one of `leadingIcon`, `avatarSrc`/`avatarName`, or `dotColor`
 * needs to be passed depending on which "leading" visual the row
 * wants; whichever is provided wins (icon > avatar > dot).
 *
 * @param {string} leadingIcon - path to a leading icon image (task/session rows)
 * @param {string} avatarName - renders an initials/photo avatar as the leading visual
 * @param {string} avatarSrc
 * @param {'blue'|'green'|'purple'|'orange'} dotColor - renders a small colored dot (activity feed)
 * @param {string} title
 * @param {string} subtitle
 * @param {React.ReactNode} badge - small inline badge next to the subtitle, e.g. "Private"
 * @param {React.ReactNode} trailing - right-aligned slot, e.g. <AvatarGroup/> or role text
 * @param {boolean} bordered - draws a bottom divider (for stacked lists)
 * @param {Function} onClick - when set, row is keyboard-activatable and pointer-styled
 */
const ListItem = ({
  leadingIcon,
  avatarSrc,
  avatarName,
  dotColor,
  title,
  subtitle,
  badge,
  trailing,
  bordered = false,
  className = '',
  onClick,
}) => {
  const [avatarFailed, setAvatarFailed] = useState(false);
  const resolvedAvatarSrc = resolveMediaUrl(avatarSrc);
  const isClickable = typeof onClick === 'function';

  useEffect(() => {
    setAvatarFailed(false);
  }, [resolvedAvatarSrc]);

  const initials = (avatarName || '')
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase())
    .join('');

  const showAvatarImage = Boolean(resolvedAvatarSrc) && !avatarFailed;

  const handleKeyDown = event => {
    if (!isClickable) return;
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      onClick(event);
    }
  };

  return (
    <div
      className={`list-item ${bordered ? 'list-item--bordered' : ''} ${
        isClickable ? 'list-item--clickable' : ''
      } ${className}`.trim()}
      onClick={isClickable ? onClick : undefined}
      onKeyDown={isClickable ? handleKeyDown : undefined}
      role={isClickable ? 'button' : undefined}
      tabIndex={isClickable ? 0 : undefined}
    >
      {leadingIcon && (
        <span className="list-item__icon">
          <img src={leadingIcon} alt="" />
        </span>
      )}

      {!leadingIcon && (avatarSrc || avatarName) && (
        <span className="list-item__avatar" title={avatarName}>
          {showAvatarImage ? (
            <img
              src={resolvedAvatarSrc}
              alt={avatarName}
              onError={() => setAvatarFailed(true)}
            />
          ) : (
            <span>{initials}</span>
          )}
        </span>
      )}

      {!leadingIcon && !avatarSrc && !avatarName && dotColor && (
        <span className={`list-item__dot list-item__dot--${dotColor}`} aria-hidden="true" />
      )}

      <div className="list-item__body">
        <p className="list-item__title">{title}</p>
        {(subtitle || badge) && (
          <div className="list-item__subtitle">
            {subtitle}
            {badge && <span className="list-item__badge">{badge}</span>}
          </div>
        )}
      </div>

      {trailing && <div className="list-item__trailing">{trailing}</div>}
    </div>
  );
};

export default ListItem;
